import React, { createContext, useContext, useMemo } from 'react';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import type { SourceRef } from '@captivateiq/assistant/types';
import { QBox } from '@captivateiq/quotient';

import { SourceCitationChip } from './TabsAIShell/ChatTab/ChatView/SourceCitationChip';
import { injectCitationMarkers } from '../utils/citations';

type CitedMarkdownProps = {
  content: string;
  sources: SourceRef[];
};

const sanitizationSchema = {
  ...defaultSchema,
  tagNames: [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    'del',
    's',
    'mark',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'a',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
    'blockquote',
    'hr',
    'code',
    'pre',
    'span',
    'sup',
  ],
  attributes: {
    ...defaultSchema.attributes,
    span: ['className'],
    sup: ['className', 'dataSourceIndex'],
  },
};

const SourcesContext = createContext<SourceRef[]>([]);

const SupWithCitation = ({ children, node, ...props }: React.HTMLAttributes<HTMLElement> & { node?: unknown }) => {
  const sources = useContext(SourcesContext);
  const sourceIndex = (props as Record<string, unknown>)['data-source-index'];
  if (sourceIndex !== undefined && sources.length > 0) {
    const idx = Number(sourceIndex);
    const sourceRef = sources[idx - 1];
    if (sourceRef) {
      return (
        <SourceCitationChip
          dataContext={sourceRef.data_context}
          index={idx}
          label={sourceRef.label}
          source={sourceRef.url}
        />
      );
    }
  }
  return <sup {...props}>{children}</sup>;
};

const markdownComponents = {
  a: ({ children, href, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a href={href} rel="noopener noreferrer" target="_blank" {...props}>
      {children}
    </a>
  ),
  table: ({ children }: React.TableHTMLAttributes<HTMLTableElement>) => (
    <QBox
      sx={{
        width: '100%',
        overflowX: 'auto',
        marginBottom: '0.5rem',
        scrollbarWidth: 'thin',
      }}
    >
      <table>{children}</table>
    </QBox>
  ),
  sup: SupWithCitation,
};

export const CitedMarkdown = ({ content, sources }: CitedMarkdownProps) => {
  const processedContent = useMemo(() => injectCitationMarkers(content, sources.length), [content, sources.length]);

  return (
    <SourcesContext.Provider value={sources}>
      <Markdown
        components={markdownComponents}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizationSchema]]}
        remarkPlugins={[remarkGfm]}
      >
        {processedContent}
      </Markdown>
    </SourcesContext.Provider>
  );
};
